import { type ComputedRef, type Ref, computed, ref } from 'vue';

import type { TemplateLibraryMode } from '@/core/conversations/template/template.utils';

/** `none` means the host's own form is showing; anything else is a TemplateLibrary subview. */
export type TemplateSubview = 'none' | TemplateLibraryMode;

/**
 * Shell state for hosting TemplateLibrary as a subview of an existing BaseModal (rather
 * than stacking a second dialog over it): which subview is showing, plus the title,
 * subtitle and back label the modal should render for it.
 *
 * Kept out of the host component so every surface that embeds the library presents the
 * same headings and back affordances.
 */
export interface MessageTemplateSubview {
  view: Ref<TemplateSubview>;
  isOpen: ComputedRef<boolean>;
  isEditingTemplate: ComputedRef<boolean>;
  title: ComputedRef<string>;
  subtitle: ComputedRef<string>;
  backButtonText: ComputedRef<string>;
  open: () => void;
  close: () => void;
  setMode: (mode: TemplateLibraryMode) => void;
}

export function useMessageTemplateSubview(options: {
  formTitle: () => string;
}): MessageTemplateSubview {
  const view = ref<TemplateSubview>('none');

  const isOpen = computed(() => view.value !== 'none');

  const title = computed(() => {
    switch (view.value) {
      case 'browse':
        return 'Use a template';
      case 'create':
        return 'Create a template';
      case 'edit':
        return 'Edit template';
      default:
        return options.formTitle();
    }
  });

  const subtitle = computed(() =>
    view.value === 'browse'
      ? 'Quickly message candidates using pre-made templates or create your own.'
      : '',
  );

  const backButtonText = computed(() => {
    switch (view.value) {
      case 'browse':
        return 'Back to editing';
      case 'create':
      case 'edit':
        return 'Back to templates';
      default:
        return '';
    }
  });

  /** True while a create/edit form is showing, i.e. back should return to the list. */
  const isEditingTemplate = computed(() => view.value === 'create' || view.value === 'edit');

  function open() {
    view.value = 'browse';
  }

  function close() {
    view.value = 'none';
  }

  function setMode(mode: TemplateLibraryMode) {
    view.value = mode;
  }

  return {
    view,
    isOpen,
    isEditingTemplate,
    title,
    subtitle,
    backButtonText,
    open,
    close,
    setMode,
  };
}
